/**
 * Pages, most used first — and inside each, its tabs, and inside those, what people did.
 *
 * ── Three levels, two of them closed ─────────────────────────────────────────────────────────
 * Everything below the page row is shut until someone opens it. That is the whole point of the
 * rework: the answer to "which pages are used" now fits on one screen, and the detail that used
 * to make that answer unreachable is one click away instead of five screens down.
 *
 * ── Reach leads, volume follows ──────────────────────────────────────────────────────────────
 * Each row leads with how many PEOPLE used the page, then the events. A page opened 900 times by
 * one person and one opened 900 times by nine are the same number and completely different facts,
 * and adoption is what this board is read for.
 *
 * Bars are scaled against one global maximum, so a bar means the same thing in every row.
 */
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@finn-auto/design-system/atoms/accordion';
import { ExternalLink } from 'lucide-react';
import { SERIES_PRIMARY, formatCompact, formatPercent } from './chartTokens';
import { UNATTRIBUTED, type FeatureNode, type PageNode, type TabNode } from './usageTree';

export function PageTree({
  pages,
  open,
  onOpenChange,
  detailFor,
  featuresFor,
}: {
  pages: PageNode[];
  /** Page keys currently expanded. Controlled, so search can open what it matched. */
  open: string[];
  onOpenChange: (open: string[]) => void;
  /**
   * The outcome panels that belong to a page — response times, sent emails, ratings. Rendered
   * inside the page they measure rather than as four more top-level sections, which is where they
   * used to sit and why the page's own question was five screens down.
   */
  detailFor?: (page: PageNode) => React.ReactNode;
  /**
   * What to render inside an opened TAB, given its section key. The view supplies it so this file
   * stays free of data fetching — and so a tab's detail is loaded when it is opened rather than
   * for every tab on the page at once.
   */
  featuresFor?: (section: string) => React.ReactNode;
}) {
  if (pages.length === 0) {
    return (
      <p className="rounded-xl border border-dashed border-zinc-300 bg-white p-8 text-center text-sm text-zinc-500">
        No page matches those filters.
      </p>
    );
  }

  const max = pages.reduce((acc, page) => Math.max(acc, page.total), 0) || 1;

  return (
    <Accordion type="multiple" value={open} onValueChange={onOpenChange} className="space-y-2">
      {pages.map((page) => {
        const detail = detailFor?.(page);
        return (
          <AccordionItem
            key={page.key}
            value={page.key}
            className="overflow-hidden rounded-xl border border-zinc-200 bg-white"
          >
            <AccordionTrigger className="px-4 py-3 hover:no-underline">
              <PageHeader page={page} max={max} />
            </AccordionTrigger>
            <AccordionContent
              alwaysMounted={false}
              className="border-t border-solid border-zinc-100 px-4 pt-4 pb-4"
            >
              {page.key === UNATTRIBUTED && (
                <p className="mb-3 text-xs text-zinc-500">
                  Recorded before events carried the page they fired on, so these cannot be placed.
                  The counts are real; only the grouping is missing, and it stops growing from here.
                </p>
              )}

              {page.tabs.length > 0 ? (
                <TabList tabs={page.tabs} featuresFor={featuresFor} />
              ) : (
                featuresFor?.(page.key) ?? <FeatureList features={page.features} />
              )}

              {/* A page WITH tabs can still have events that carried none — its own row, never
                  folded into whichever tab happens to be first. */}
              {page.tabs.length > 0 && page.features.length > 0 && (
                <div className="mt-4 border-t border-zinc-100 pt-3">
                  <p className="mb-2 text-xs font-medium text-zinc-500">No tab recorded</p>
                  <FeatureList features={page.features} />
                </div>
              )}

              {detail && <div className="mt-5 border-t border-zinc-100 pt-4">{detail}</div>}

              {page.path && (
                <a
                  href={page.path}
                  className="mt-4 inline-flex items-center gap-1 text-xs text-zinc-500 underline underline-offset-2 hover:text-zinc-700"
                >
                  Open {page.label} <ExternalLink className="h-3 w-3" />
                </a>
              )}
            </AccordionContent>
          </AccordionItem>
        );
      })}
    </Accordion>
  );
}

function PageHeader({ page, max }: { page: PageNode; max: number }) {
  return (
    <div className="flex flex-1 flex-col gap-1.5 pr-3 text-left">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <span className="flex items-baseline gap-2">
          <span className="text-sm font-semibold text-zinc-900">{page.label}</span>
          <span className="text-xs font-normal text-zinc-400">{page.area}</span>
        </span>
        <span
          className="text-xs font-normal text-zinc-500"
          style={{ fontVariantNumeric: 'tabular-nums' }}
        >
          {page.users !== null && (
            <>
              <span className="font-medium text-zinc-900">{page.users}</span>
              {page.users === 1 ? ' person' : ' people'} ·{' '}
            </>
          )}
          {formatCompact(page.total)} events · {formatPercent(page.share)} of all use
        </span>
      </div>
      <Bar value={page.total} max={max} />
    </div>
  );
}

/** The page's share of the busiest page, as one line. No axis: the number is right beside it. */
function Bar({ value, max }: { value: number; max: number }) {
  return (
    <div className="h-1.5 w-full overflow-hidden rounded-full bg-zinc-100">
      <div
        className="h-full rounded-full"
        style={{ width: `${Math.max((value / max) * 100, 1)}%`, backgroundColor: SERIES_PRIMARY }}
      />
    </div>
  );
}

/**
 * A tab, and — where the route has one — its sub-tabs.
 *
 * The TA Cockpit's Tracking is Delivery and Hiring; its Role Prioritisation is the score and the
 * cluster check-in. Those are separate places to work, and reporting the tab alone answered "was
 * the Cockpit opened" when the question was which half of it.
 */
function TabList({
  tabs,
  nested = false,
  featuresFor,
}: {
  tabs: TabNode[];
  nested?: boolean;
  featuresFor?: (section: string) => React.ReactNode;
}) {
  const max = tabs.reduce((acc, tab) => Math.max(acc, tab.total), 0) || 1;
  return (
    <Accordion type="multiple" className="space-y-1.5">
      {tabs.map((tab) => (
        <AccordionItem
          key={tab.section}
          value={tab.section}
          className={`overflow-hidden rounded-lg border border-zinc-100 ${
            nested ? 'bg-white' : 'bg-zinc-50/60'
          }`}
        >
          <AccordionTrigger className="px-3 py-2 hover:no-underline">
            <div className="flex flex-1 flex-col gap-1.5 pr-3 text-left">
              <div className="flex flex-wrap items-baseline justify-between gap-x-3">
                <span className="text-xs font-semibold text-zinc-900">
                  {tab.tab === UNATTRIBUTED ? 'No tab recorded' : tab.label}
                </span>
                <span
                  className="text-xs font-normal text-zinc-500"
                  style={{ fontVariantNumeric: 'tabular-nums' }}
                >
                  {tab.users !== null && `${tab.users} ${tab.users === 1 ? 'person' : 'people'} · `}
                  {formatCompact(tab.total)}
                </span>
              </div>
              <Bar value={tab.total} max={max} />
            </div>
          </AccordionTrigger>
          <AccordionContent
            alwaysMounted={false}
            className="border-t border-solid border-zinc-100 bg-white px-3 pt-3 pb-3"
          >
            {tab.subTabs.length > 0 && (
              <TabList tabs={tab.subTabs} nested featuresFor={featuresFor} />
            )}
            {/* Events on the tab itself, beside its sub-tabs rather than folded into one of
                them — a visit recorded before the sub-tab was in the URL belongs to neither. */}
            {tab.features.length > 0 && (
              <div className={tab.subTabs.length > 0 ? 'mt-3 border-t border-zinc-100 pt-3' : ''}>
                {tab.subTabs.length > 0 && (
                  <p className="mb-2 text-xs font-medium text-zinc-500">No sub-tab recorded</p>
                )}
                {featuresFor?.(tab.section) ?? <FeatureList features={tab.features} />}
              </div>
            )}
            {tab.subTabs.length === 0 && tab.features.length === 0 && (
              <FeatureList features={[]} />
            )}
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
}

/**
 * The deepest level: what people actually did.
 *
 * The explanation sits under the name rather than in a tooltip. At this depth somebody has clicked
 * twice to get here — they are reading, not scanning — and `ta_requests_draft_rated` means nothing
 * without its sentence. A number nobody can interpret is not a measurement.
 */
function FeatureList({ features }: { features: FeatureNode[] }) {
  if (features.length === 0) {
    return <p className="py-2 text-xs text-zinc-500">Nothing recorded here in this range.</p>;
  }
  return (
    <ul className="divide-y divide-zinc-100">
      {features.map((feature) => (
        <li key={feature.event} className="flex items-baseline justify-between gap-4 py-2">
          <div className="min-w-0">
            <p className="font-mono text-xs text-zinc-700">{feature.event}</p>
            {feature.description && (
              <p className="mt-0.5 max-w-[70ch] text-xs text-zinc-500">{feature.description}</p>
            )}
          </div>
          <span
            className="shrink-0 text-xs font-medium text-zinc-900"
            style={{ fontVariantNumeric: 'tabular-nums' }}
          >
            {formatCompact(feature.count)}
          </span>
        </li>
      ))}
    </ul>
  );
}
